/**
 * @voltxampere/engine — ERC orchestrator (Sprint 56)
 *
 * VISION §6.F P0. Runs every registered rule against the circuit,
 * collects violations, returns an ERCReport. Pure function; no
 * state, no I/O. Safe to call from worker context (e.g. on DC OP
 * completion) without async coordination.
 *
 * Adding a new rule:
 *   1. Create `rules/<rule-id>.ts` exporting an ERCRule.
 *   2. Add the literal to ERCRuleId union in `types.ts`.
 *   3. Register the rule object in ALL_RULES below.
 *   4. Add i18n entries `erc.<rule-id>.{message,suggestion}` in
 *      packages/shared/src/i18n/bundles/{tr,en}.ts.
 *   5. Add Recovery Center suggestion in
 *      packages/shared/src/recovery-suggestions/index.ts.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { floatingNodeRule } from "./rules/floating-node.js";
import { missingGroundRule } from "./rules/missing-ground.js";
import { unconnectedPinRule } from "./rules/unconnected-pin.js";
import { shortCircuitRule } from "./rules/short-circuit.js";
import { saturationRule } from "./rules/saturation.js";
import { overvoltageRule } from "./rules/overvoltage.js";
import { overheatingRule } from "./rules/overheating.js";
/**
 * S56a MVP rule registry. Order is stable for deterministic
 * violation ordering in test snapshots.
 */
export const ALL_RULES = Object.freeze([
    missingGroundRule,
    unconnectedPinRule,
    floatingNodeRule,
    shortCircuitRule,
    saturationRule,
    overvoltageRule,
    overheatingRule,
]);
/**
 * Run every registered rule against the circuit. Topology-only rules
 * always run; DC-aware rules (requiresDCResult) run only when
 * `dcResult` is provided.
 *
 * Never throws — rule check functions are contract-bound pure
 * functions. Caller can safely render the report regardless of
 * which rules fired.
 */
export function runERC(circuit, dcResult) {
    const violations = ALL_RULES.flatMap((rule) => rule.requiresDCResult && dcResult === undefined ? [] : rule.check(circuit, dcResult));
    return {
        violations,
        dcAwareRulesRan: dcResult !== undefined,
        checkedAt: Date.now(),
    };
}
//# sourceMappingURL=index.js.map